import {
  BooleanPropertyDefinition,
  Identifiable,
  OptionsPropertyDefinition,
  StringPropertyDefinition
} from '@shared/models/schema-property-definition';

import { Dependent } from './dependent';
import { Relationship } from './relationship';
import { Residence } from './residence';
import { ContactType } from '../contactType';
import { ContactReferrerArrangement } from '../referrer-arrangement';

export * from './dependent';
export * from './relationship';
export * from './residence';

export namespace Individual {
  export interface SchemaPropertyDefinition {
    name: {
      title: StringPropertyDefinition;
      first: StringPropertyDefinition;
      last: StringPropertyDefinition;
      middle: StringPropertyDefinition;
      preferred: StringPropertyDefinition;
    };

    gender: OptionsPropertyDefinition;
    birthDate: StringPropertyDefinition;
    countryOfBirth: StringPropertyDefinition;
    contactType: OptionsPropertyDefinition;
    status: OptionsPropertyDefinition;
    sendMarketingCommunications: BooleanPropertyDefinition;

    contact: {
      homePhone: StringPropertyDefinition;
      workPhone: StringPropertyDefinition;
      mobile: StringPropertyDefinition;
      email: StringPropertyDefinition;
      preferredMethod: StringPropertyDefinition;
    };

    identification: {
      driverLicence: {
        number: StringPropertyDefinition;
        cardNumber: StringPropertyDefinition;
        expiry: StringPropertyDefinition;
        type: StringPropertyDefinition;
        issuer: StringPropertyDefinition;
      };
      passport: {
        number: StringPropertyDefinition;
        expiry: StringPropertyDefinition;
      };
    };

    bankingInstitution: StringPropertyDefinition;
    permanentResident: BooleanPropertyDefinition;
    citizen: BooleanPropertyDefinition;
    repayingStudentLoan: BooleanPropertyDefinition;
    referrerArrangement: ContactReferrerArrangement.SchemaPropertyDefinition;
  }

  export interface Contact {
    homePhone: string | null;
    workPhone: string | null;
    mobile: string | null;
    email: string | null;
    preferredMethod: string | null;
  }
  export interface DriverLicence {
    number: string | null;
    cardNumber: string | null;
    expiry: string | null;
    type: string | null;
    issuer: string | null;
  }
  export interface Passport {
    number: string | null;
    expiry: string | null;
  }

  export interface Identification {
    driverLicence: DriverLicence;
    passport: Passport;
  }

  export interface Name {
    title: string | null;
    first: string | null;
    last: string | null;
    middle: string | null;
    preferred: string | null;
  }

  export interface SchemaContract {
    name: Name;
    gender: string | null;
    birthDate: string | null;
    countryOfBirth: string | null;
    contactType: string | null;
    status: string | null;
    sendMarketingCommunications: boolean | null;
    contact: Contact;
    identification: Identification;
    bankingInstitution: string | null;
    permanentResident: boolean;
    citizen: boolean;
    repayingStudentLoan: boolean;
    referrerArrangement: ContactReferrerArrangement.SchemaContract | null;
    dependents: Dependent.CollectionSchemaContract;
    residences: Residence.CollectionSchemaContract;
    relationships: Relationship.SchemaContract;
    nmbAffiliateId?: string | null;
  }

  export interface Model extends Identifiable {
    name: Name;
    gender: string | null;
    birthDate: string | null;
    countryOfBirth: string | null;
    contactType: ContactType | null;
    status: string | null;
    sendMarketingCommunications: boolean | null;
    contact: Contact;
    identification: Identification;
    bankingInstitution: string | null;
    permanentResident: boolean;
    citizen: boolean;
    repayingStudentLoan: boolean;
    referrerArrangement: ContactReferrerArrangement.Model | null;
    dependents: Dependent.Model[];
    residences: Residence.Model[];
    relationships: Relationship.Model[];
    nmbAffiliateId: string | null;
  }

  export interface Summary {
    id: string;
    name: string;
  }

  export function toModel(id: string, i: SchemaContract): Model {
    return {
      id,
      name: i.name ?? null,
      bankingInstitution: i.bankingInstitution ?? null,
      birthDate: i.birthDate ?? null,
      citizen: i.citizen ?? null,
      countryOfBirth: i.countryOfBirth ?? null,
      contact: i.contact ?? {},
      status: i.status ?? null,
      nmbAffiliateId: i.nmbAffiliateId ?? null,
      sendMarketingCommunications: i.sendMarketingCommunications ?? null,
      identification: i.identification ?? {},
      permanentResident: i.permanentResident ?? null,
      repayingStudentLoan: i.repayingStudentLoan ?? null,
      gender: i.gender ?? null,
      contactType: (i.contactType as ContactType) ?? null,
      referrerArrangement: ContactReferrerArrangement.toModel(
        i.referrerArrangement
      ),
      dependents: Dependent.toModel(i.dependents),
      residences: Residence.toModel(i.residences),
      relationships: Relationship.toModel(i.relationships)
    };
  }

  export function toSchemaContract(i: Partial<Model>): Partial<SchemaContract> {
    return {
      ...i,
      referrerArrangement: i.referrerArrangement
        ? ContactReferrerArrangement.toSchemaContract(i.referrerArrangement)
        : i.referrerArrangement,
      dependents: i.dependents
        ? Dependent.toCollectionSchemaContracts(i.dependents)
        : undefined,
      residences: i.residences
        ? Residence.toSchemaContracts(i.residences)
        : undefined,
      relationships: i.relationships
        ? Relationship.toSchemaContracts(i.relationships)
        : undefined
    };
  }
}
